import { useQuery } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";
import { StatGrid } from "@/components/StatGrid";
import { POLL } from "@/lib/format";
import { RQ } from "@/modules/registry/parts_common";

import { registry } from "@/api/client";
import { Donut } from "@/components/Donut";

const COLORS = ["var(--teal)", "var(--info)", "var(--orange)", "var(--ok)", "var(--bad)", "var(--teal-deep)"];

function Stat({ n, l, tone }: { n?: number; l: string; tone?: "accent" | "warn" }) {
  const box =
    tone === "accent"
      ? { borderColor: "var(--teal-line)", background: "var(--teal-soft)" }
      : tone === "warn"
        ? { borderColor: "var(--warn-line)", background: "var(--warn-soft)" }
        : {};
  const nColor = tone === "accent" ? "var(--teal)" : tone === "warn" ? "var(--warn)" : "var(--t-bright)";
  return (
    <div className="panel" style={{ padding: "15px 16px", ...box }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: nColor, letterSpacing: "-.5px", fontVariantNumeric: "tabular-nums" }}>
        {n ?? "—"}
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--t-dim)", marginTop: 3 }}>
        {l}
      </div>
    </div>
  );
}

export function RegistryOverviewPage() {
  const q = useQuery({ queryKey: [...RQ, "stats"], queryFn: registry.stats, refetchInterval: POLL.DASHBOARD });
  const pst = useQuery({ queryKey: ["registry", "parts", "stats"], queryFn: registry.partsStats, refetchInterval: POLL.DASHBOARD });
  const s = q.data;
  const dist = (s?.distribution ?? []).map((d, i) => ({
    label: d.name,
    value: d.count,
    color: d.company_id ? COLORS[i % COLORS.length] : "var(--t-dim)",
  }));
  const useVsInv = s
    ? [
        { label: "In use · allocated", value: s.in_use, color: "var(--teal)" },
        { label: "Inventory", value: s.inventory, color: "var(--t-dim)" },
      ]
    : [];

  return (
    <div className="mpage on">
      <PageHead eyebrow="Registry" title="Overview" />
      <StatGrid stats={[
        { k: "Assets", v: pst.data?.assets ?? "—", s: `${pst.data?.assets_deployed ?? "—"} deployed · ${pst.data?.assets_inventory ?? "—"} in inventory` },
        { k: "Parts", v: pst.data?.parts ?? "—", s: `${pst.data?.inventory ?? "—"} in inventory · ${pst.data?.installed ?? "—"} installed` },
      ]} style={{ marginTop: 16 }} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginTop: 8 }}>
        <Stat n={s?.registered} l="Registered" />
        <Stat n={s?.in_use} l="In use · allocated" tone="accent" />
        <Stat n={s?.inventory} l="In inventory" />
        <Stat n={s?.unregistered_seen} l="Seen · unregistered" tone="warn" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 16, marginTop: 22, alignItems: "stretch" }}>
        <div className="panel panel-pad" style={{ display: "flex", flexDirection: "column" }}>
          <div className="section-label flush">Devices across companies</div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Donut slices={dist} />
          </div>
        </div>
        <div className="panel panel-pad" style={{ display: "flex", flexDirection: "column" }}>
          <div className="section-label flush">In use vs inventory</div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Donut slices={useVsInv} />
          </div>
        </div>
        <div className="panel panel-pad" style={{ display: "flex", flexDirection: "column" }}>
          <div className="section-label flush">Avg enrollment → allocation</div>
          <div style={{ fontSize: 38, fontWeight: 800, color: "var(--t-bright)", letterSpacing: "-1px", marginTop: 18, fontVariantNumeric: "tabular-nums" }}>
            {s?.avg_enroll_to_allocate_days ?? "—"}
            <span style={{ fontSize: 15, color: "var(--t-mute)", fontWeight: 700, marginLeft: 4 }}>days</span>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 12 }}>
            Time from a device being enrolled (into inventory) to its allocation to a customer.
          </div>
        </div>
      </div>
    </div>
  );
}
